/**
 * SB6-ANA-VIEW-01-FIX — right-click marquee-zoom commit decision.
 *
 * Replaces the previous `computeMarqueeMinSizePx`, which rejected any
 * marquee smaller than `viewerWidth / maxZoomFactor` (≈160 px at the
 * 500 % default). That rejection silently killed the natural gesture —
 * drawing a *small* box around a defect to zoom in tightly — and
 * directly violated the locked spec R58-L-16 AC2 ("a box that would
 * need > max-zoom clamps to max-zoom", NOT a no-op).
 *
 * The correct design:
 *   * Over-zoom is already prevented by OSD's
 *     `viewport.fitBoundsWithConstraints`, which clamps the committed
 *     zoom to the user's `maxZoomPercent`. So a tiny box just lands
 *     exactly at max-zoom — which IS what OQ-SB6-C6 asked for.
 *   * The ONLY thing the commit path needs to decide is whether the
 *     gesture was an intentional DRAG or an accidental CLICK (R58-L-16
 *     AC3 — a right-click with no drag must not zoom, and it arms the
 *     double-right-click reset). That is a small, fixed anti-jitter
 *     floor, independent of max-zoom.
 */

/**
 * CSS-pixel anti-jitter floor. A right-mouse gesture that moved less
 * than this on BOTH axes is treated as a click, not a drag. Kept small
 * so the gesture stays forgiving — any deliberate box commits. (Story
 * 032 originally used 20 px; 16 px is marginally more forgiving while
 * still discarding the few-pixel jitter of a stationary right-click.)
 */
export const MARQUEE_DRAG_THRESHOLD_PX = 16;

/**
 * True when a right-mouse marquee of `widthPx × heightPx` (CSS px) is an
 * intentional drag worth committing as a zoom. A click — near-zero on
 * both axes — returns false so the viewport is left untouched and the
 * double-right-click-reset gesture stays intact.
 *
 * Requires BOTH axes to clear the floor: a near-degenerate sliver (e.g.
 * a 2 px-tall horizontal swipe) is treated as a click, not a zoom box.
 */
export function marqueeIsDragGesture(
  widthPx: number,
  heightPx: number,
): boolean {
  if (!Number.isFinite(widthPx) || !Number.isFinite(heightPx)) return false;
  return (
    widthPx >= MARQUEE_DRAG_THRESHOLD_PX &&
    heightPx >= MARQUEE_DRAG_THRESHOLD_PX
  );
}
